import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../axios';
import type {
  AttendanceRecord,
  CalendarDay,
  LeaveType,
  LeaveBalance,
  LeaveRequest,
  TeamAttendanceMember,
  YearlySummary,
  EmployeeYearlySummary,
} from '../types/attendanceTypes';

export const checkIn = createAsyncThunk<AttendanceRecord, void, { rejectValue: string }>(
  'attendance/checkIn',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/v1/employee/attendance/checkin');
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Check-in failed');
    }
  }
);

export const checkOut = createAsyncThunk<AttendanceRecord, void, { rejectValue: string }>(
  'attendance/checkOut',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/v1/employee/attendance/checkout');
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Check-out failed');
    }
  }
);

export const fetchMyAttendance = createAsyncThunk<
  AttendanceRecord[],
  { year?: number; month?: number },
  { rejectValue: string }
>('attendance/fetchMyAttendance', async (params, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get('/v1/employee/attendance', { params });
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch attendance');
  }
});

export const fetchMyCalendar = createAsyncThunk<
  CalendarDay[],
  { year: number; month: number },
  { rejectValue: string }
>('attendance/fetchMyCalendar', async ({ year, month }, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(`/v1/employee/attendance/calendar/${year}/${month}`);
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch calendar');
  }
});

export const requestLeave = createAsyncThunk<
  LeaveRequest,
  { startDate: string; endDate: string; reason: string; leaveTypeId?: string; halfDayPeriod?: 'FIRST_HALF' | 'SECOND_HALF' },
  { rejectValue: string }
>('attendance/requestLeave', async (payload, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.post('/v1/employee/leave/request', payload);
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to submit leave request');
  }
});

export const fetchMyLeaveRequests = createAsyncThunk<LeaveRequest[], void, { rejectValue: string }>(
  'attendance/fetchMyLeaveRequests',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/v1/employee/leave/requests');
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch leave requests');
    }
  }
);

export const fetchMyLeaveBalance = createAsyncThunk<LeaveBalance[], { year?: number }, { rejectValue: string }>(
  'attendance/fetchMyLeaveBalance',
  async (params, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/v1/employee/leave/balance', { params });
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch leave balance');
    }
  }
);

export const fetchLeaveTypes = createAsyncThunk<LeaveType[], void, { rejectValue: string }>(
  'attendance/fetchLeaveTypes',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/v1/employee/leave/types');
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch leave types');
    }
  }
);

export const fetchTeamAttendance = createAsyncThunk<
  TeamAttendanceMember[],
  { managerId: string; year?: number; month?: number },
  { rejectValue: string }
>('attendance/fetchTeamAttendance', async ({ managerId, ...params }, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(`/v1/admin/attendance/team/${managerId}`, { params });
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch team attendance');
  }
});

export const fetchTeamLeaveRequests = createAsyncThunk<
  LeaveRequest[],
  { managerId: string; status?: string },
  { rejectValue: string }
>('attendance/fetchTeamLeaveRequests', async ({ managerId, ...params }, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(`/v1/admin/leave/team/${managerId}`, { params });
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch team leave requests');
  }
});

export const reviewLeaveRequest = createAsyncThunk<
  LeaveRequest,
  { requestId: string; status: 'APPROVED' | 'REJECTED'; reviewNote?: string },
  { rejectValue: string }
>('attendance/reviewLeaveRequest', async ({ requestId, ...body }, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.put(`/v1/admin/leave/request/${requestId}`, body);
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to review leave request');
  }
});

export const fetchAllAttendance = createAsyncThunk<
  { records: AttendanceRecord[]; totalEmployees: number },
  { year?: number; month?: number; employeeId?: string },
  { rejectValue: string }
>('attendance/fetchAllAttendance', async (params, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get('/v1/admin/attendance/all', { params });
    return { records: res.data.data, totalEmployees: res.data.totalEmployees ?? 0 };
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch all attendance');
  }
});

export const fetchAllLeaveRequests = createAsyncThunk<
  LeaveRequest[],
  { status?: string },
  { rejectValue: string }
>('attendance/fetchAllLeaveRequests', async (params, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get('/v1/admin/leave/all', { params });
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch all leave requests');
  }
});

export const fetchMyYearlySummary = createAsyncThunk<
  YearlySummary,
  { year: number },
  { rejectValue: string }
>('attendance/fetchMyYearlySummary', async ({ year }, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get('/v1/employee/attendance/summary/yearly', { params: { year } });
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch yearly summary');
  }
});

export const fetchTeamYearlySummary = createAsyncThunk<
  EmployeeYearlySummary[],
  { managerId: string; year: number },
  { rejectValue: string }
>('attendance/fetchTeamYearlySummary', async ({ managerId, year }, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(`/v1/admin/attendance/summary/team/${managerId}`, { params: { year } });
    return res.data.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch team yearly summary');
  }
});

export const fetchAllYearlySummary = createAsyncThunk<
  { data: EmployeeYearlySummary[]; total: number },
  { year: number; page?: number; limit?: number },
  { rejectValue: string }
>('attendance/fetchAllYearlySummary', async (params, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get('/v1/admin/attendance/summary/all', { params });
    return { data: res.data.data, total: res.data.total };
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch yearly summaries');
  }
});
